import { apiService } from "../utils/api/api.service";
import { ApiResponse } from "../features/auth/interfaces/auth.interface";
import { TokenManager } from "../utils/storage/tokenmanager.storage";

export interface Session {
  id: string;
  browser: string;
  browserVersion: string;
  device: string;
  deviceType: "desktop" | "mobile" | "tablet";
  location: string;
  country: string;
  city: string;
  os: string;
  osVersion: string;
  isCurrent: boolean;
  lastActivityAt: string;
  createdAt: string;
  expiresAt: string | null;
}

export interface PaginationMetadata {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ListSessionsApiResponse {
  success: boolean;
  data?:
    | Session[]
    | {
        sessions: Session[];
        pagination: PaginationMetadata;
      };
  pagination?: PaginationMetadata;
  message?: string;
}

export const sessionService = {
  /**
   * Get all user sessions
   */
  async getSessions(page: number = 1, limit: number = 15): Promise<{
    sessions: Session[];
    pagination: PaginationMetadata;
  }> {
    const refreshToken = TokenManager.getRefreshToken();
    const response = await apiService.post<ListSessionsApiResponse>(
      `/sessions?page=${page}&limit=${limit}`,
      { refreshToken },
    );

    if (response.data.success && response.data.data) {
      // Standardized shape A: { data: Session[], pagination }
      if (Array.isArray(response.data.data)) {
        const sessions = response.data.data;
        const pagination =
          response.data.pagination ?? {
            total: sessions.length,
            page,
            limit,
            totalPages: 1,
          };
        return { sessions, pagination };
      }

      // Standardized shape B: { data: { sessions, pagination } }
      const nested = response.data.data as {
        sessions?: Session[];
        pagination?: PaginationMetadata;
      };
      if (Array.isArray(nested.sessions)) {
        const sessions = nested.sessions;
        const pagination =
          nested.pagination ??
          response.data.pagination ?? {
            total: sessions.length,
            page,
            limit,
            totalPages: 1,
          };
        return { sessions, pagination };
      }
    }

    throw new Error(response.data.message || "Failed to fetch sessions");
  },

  /**
   * Revoke a specific session
   */
  async revokeSession(sessionId: string): Promise<void> {
    const response = await apiService.delete<ApiResponse>(
      `/sessions/${sessionId}`,
    );
    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to revoke session");
    }
  },

  /**
   * Revoke all other sessions (keep current)
   */
  async revokeAllOtherSessions(): Promise<void> {
    const response = await apiService.delete<ApiResponse>("/sessions");
    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to revoke sessions");
    }
  },

  /**
   * Update session activity
   */
  async updateActivity(): Promise<void> {
    await apiService.post("/sessions/activity", {});
  },
};
